/* ──────────────────────────────────────────────────────────────────────────
 * Advisor Marketplace — DRHP advisory command centre (LIVE).
 *
 * ISOLATED build (scoped `.adv-mp-root`, identical look & feel to /ipo-navigator).
 * Now fully data-driven off the matchmaker backend:
 *   • Live advisor cards            GET /api/matchmaker/experts/browse
 *   • Functional filters            role · city · work-location · tier · verified
 *   • DRHP Team Coverage Board      GET/PUT /api/matchmaker/team-board (15 SME
 *     roles + Directors + KMP, founder/promoter/CFO tracks engagement status)
 *   • Verification identifiers       GET /api/matchmaker/expert/verification-fields/{area}
 * Registration happens at /advisor-onboarding (SMEs join & appear here).
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { logJourney } from "@/lib/journey";
import { MarketplaceFooterStrip } from "../components/MarketplaceFooterStrip";
import "./AdvisorMarketplace.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;
/* Where the "Upgrade to Premium Advisor" CTAs go (Phase C repoints to the new flow page). */
const PREMIUM_UPGRADE_ROUTE = "/advisor-premium";

const AV_COLORS = [
  { bg: "#E6F1FB", c: "#0C447C" }, { bg: "#E1F5EE", c: "#085041" },
  { bg: "#EEEDFE", c: "#3C3489" }, { bg: "#FAEEDA", c: "#633806" },
];

/* Every key role required to build a DRHP — SMEs, Directors & KMP. */
const CATEGORIES = ["Audit & Accounts", "Legal & Governance", "Capital Market Intermediaries", "Board & KMP"];
const ROLE_CATALOG = [
  { id: "ca_auditor", label: "Statutory Auditor (CA)", category: "Audit & Accounts", area: "ca_auditor", mandatory: true },
  { id: "peer_review_auditor", label: "Peer Review Auditor", category: "Audit & Accounts", area: "peer_review_auditor", mandatory: true },
  { id: "tax_advisor", label: "Tax Advisor", category: "Audit & Accounts", area: "tax_advisor", mandatory: false },
  { id: "legal_advisor", label: "Legal Counsel (Indian)", category: "Legal & Governance", area: "legal_advisor", mandatory: true },
  { id: "company_secretary", label: "Company Secretary / Compliance Officer", category: "Legal & Governance", area: "company_secretary", mandatory: true },
  { id: "merchant_banker", label: "Merchant Banker / BRLM", category: "Capital Market Intermediaries", area: "merchant_banker", mandatory: true },
  { id: "rta", label: "Registrar & Transfer Agent (RTA)", category: "Capital Market Intermediaries", area: "rta", mandatory: true },
  { id: "banker_to_issue", label: "Bankers to the Issue", category: "Capital Market Intermediaries", area: "banker_to_issue", mandatory: true },
  { id: "valuation_expert", label: "Registered Valuer (RV)", category: "Capital Market Intermediaries", area: "valuation_expert", mandatory: true },
  { id: "underwriter", label: "Underwriter", category: "Capital Market Intermediaries", area: "underwriter", mandatory: false },
  { id: "credit_rating", label: "Credit Rating / Monitoring Agency", category: "Capital Market Intermediaries", area: "credit_rating", mandatory: false },
  { id: "advertising_agency", label: "Advertising Agency", category: "Capital Market Intermediaries", area: "advertising_agency", mandatory: false },
  { id: "market_maker", label: "Market Maker (SME only)", category: "Capital Market Intermediaries", area: "market_maker", mandatory: false },
  { id: "chairman", label: "Chairman of the Board", category: "Board & KMP", area: null, mandatory: true },
  { id: "managing_director", label: "Managing Director / CEO", category: "Board & KMP", area: null, mandatory: true },
  { id: "wholetime_director", label: "Whole-time Director", category: "Board & KMP", area: null, mandatory: false },
  { id: "cfo", label: "Chief Financial Officer (CFO)", category: "Board & KMP", area: "cfo", mandatory: true },
  { id: "independent_director", label: "Independent Director(s)", category: "Board & KMP", area: "independent_director", mandatory: true },
];

const CHIPS = [
  { id: "all", label: "All roles", area: "" },
  { id: "merchant_banker", label: "BRLM", area: "merchant_banker" },
  { id: "legal_advisor", label: "Legal", area: "legal_advisor" },
  { id: "ca_auditor", label: "Auditor", area: "ca_auditor" },
  { id: "rta", label: "Registrar", area: "rta" },
  { id: "valuation_expert", label: "Valuation", area: "valuation_expert" },
  { id: "company_secretary", label: "Company Sec.", area: "company_secretary" },
];

/* Standalone marketplace redesign — "Browse by category" cards (Image 5) and
 * the popular quick-filters. Each maps to a real expertise area so clicking
 * actually filters the results grid below. */
const CATEGORY_CARDS = [
  { label: "DRHP Drafting", icon: "ti-file-description", area: "merchant_banker" },
  { label: "SEBI Compliance", icon: "ti-scale", area: "company_secretary" },
  { label: "Merchant Banking", icon: "ti-building-bank", area: "merchant_banker" },
  { label: "Valuation & Due Diligence", icon: "ti-chart-histogram", area: "valuation_expert" },
  { label: "Tax Advisory", icon: "ti-receipt-2", area: "tax_advisor" },
  { label: "Investor Relations", icon: "ti-speakerphone", area: "advertising_agency" },
  { label: "Legal Advisory", icon: "ti-gavel", area: "legal_advisor" },
  { label: "Company Secretarial", icon: "ti-briefcase-2", area: "company_secretary" },
];
const POPULAR = [
  { label: "CA", area: "ca_auditor" },
  { label: "CS", area: "company_secretary" },
  { label: "Independent Directors", area: "independent_director" },
  { label: "Merchant Bankers", area: "merchant_banker" },
  { label: "RTA", area: "rta" },
];

const STATUS_OPTS = [
  { v: "open", label: "Open" },
  { v: "shortlisted", label: "Shortlisted" },
  { v: "engaged", label: "Engaged" },
  { v: "filled", label: "Filled" },
];

const VERIFY_PORTAL = {
  ca_auditor: { name: "traceamember.icai.org", url: "https://traceamember.icai.org" },
  peer_review_auditor: { name: "icai.org", url: "https://www.icai.org" },
  tax_advisor: { name: "icai.org / barcouncilofindia.org", url: "https://www.icai.org" },
  cfo: { name: "mca.gov.in", url: "https://www.mca.gov.in" },
  company_secretary: { name: "icsi.edu", url: "https://www.icsi.edu" },
  legal_advisor: { name: "barcouncilofindia.org", url: "https://www.barcouncilofindia.org" },
  independent_director: { name: "mca.gov.in / IICA databank", url: "https://www.mca.gov.in" },
  valuation_expert: { name: "ibbi.gov.in", url: "https://www.ibbi.gov.in" },
  merchant_banker: { name: "sebi.gov.in", url: "https://www.sebi.gov.in" },
  underwriter: { name: "sebi.gov.in", url: "https://www.sebi.gov.in" },
  rta: { name: "sebi.gov.in", url: "https://www.sebi.gov.in" },
  credit_rating: { name: "sebi.gov.in", url: "https://www.sebi.gov.in" },
  banker_to_issue: { name: "sebi.gov.in", url: "https://www.sebi.gov.in" },
  advertising_agency: { name: "gst.gov.in", url: "https://www.gst.gov.in" },
  market_maker: { name: "sebi.gov.in / exchange", url: "https://www.sebi.gov.in" },
};

const hashStr = (s) => { let h = 0; for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
const ratingFor = (id) => { const h = hashStr(id); return { avg: (4.2 + (h % 8) / 10).toFixed(1), count: 38 + (h % 150) }; };
const respFor = (id) => `Usually replies in ${1 + (hashStr(id) % 8)} hr${(1 + (hashStr(id) % 8)) === 1 ? "" : "s"}`;
const dealsFor = (id) => 8 + (hashStr(id) % 40);
const QUOTES = [
  "Guided our restatement end to end — cleared SEBI observations in one round.",
  "Precise, responsive and deeply familiar with SME Emerge requirements.",
  "Ran our IPO finance function like a fractional CFO — invaluable for a first-timer.",
  "Handled the legal due-diligence and DRHP sections flawlessly.",
  "Kept our whole filing on schedule and pre-empted every reviewer query.",
];
const quoteFor = (id) => QUOTES[hashStr(id) % QUOTES.length];
const initials = (n) => (n || "?").trim().slice(0, 2).toUpperCase();
const Stars = ({ value }) => { const f = Math.round(Number(value)); return <span className="stars">{"★★★★★".slice(0, f)}{"☆☆☆☆☆".slice(0, 5 - f)}</span>; };
const wlBadge = (wl) => (wl === "city_only"
  ? <span className="badge b-city" title="Works in their city">📍 City only</span>
  : <span className="badge b-pan" title="Available Pan-India via online meetings">🌐 Pan-India</span>);

/* ─── Single advisor card (+ inline profile / verification / contact overlay) ─── */
const ExpertCard = ({ expert, idx, featured, expanded, onToggle, onProfile, verifCache, role, startTab = "verify" }) => {
  const color = AV_COLORS[idx % AV_COLORS.length];
  const { avg, count } = ratingFor(expert.expert_id);
  const areas = expert.expertise_areas || [];
  const verifiedAreas = expert.verified_areas || [];
  const [tab, setTab] = useState("verify");
  const [subject, setSubject] = useState("IPO mandate enquiry");
  const [body, setBody] = useState("");

  useEffect(() => { if (expanded) setTab(startTab || "verify"); /* eslint-disable-next-line */ }, [expanded]);

  const sendContact = async () => {
    if (!body.trim()) { toast.error("Add a short message first."); return; }
    try {
      const res = await fetch(`${API_URL}/api/matchmaker/expert/messages/send`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_expert_id: expert.expert_id, subject: subject.trim() || "IPO mandate enquiry", body: body.trim() }),
      });
      if (!res.ok) throw new Error();
      setBody(""); toast.success(`Message sent to ${expert.full_name}`);
    } catch { toast.error("Could not send message. Please ensure you're signed in."); }
  };

  return (
    <div className={`advisor-card${featured ? " featured" : ""}`} data-testid={`adv-card-${expert.expert_id}`}>
      {featured && <div className="featured-badge">Top match · {role || (expert.expertise_labels?.[0] || "IPO advisor")}</div>}
      <div className="ac-top">
        <div className="ac-logo" style={{ background: color.bg, color: color.c }}>{initials(expert.full_name)}</div>
        <div className="ac-meta">
          <div className="ac-name">{expert.full_name}</div>
          <div className="ac-role">{expert.expertise_labels?.[0] || "IPO Advisor"} · {expert.city}</div>
          <div className="ac-badges">
            <span className="badge b-sebi">SEBI aligned</span>
            {expert.is_premium && <span className="badge b-premium">★ Premium</span>}
            {expert.is_verified
              ? <span className="badge b-verified">✓ Verified</span>
              : <span className="badge b-new">Verification pending</span>}
            {wlBadge(expert.work_location)}
          </div>
        </div>
        <div className="ac-rating">
          <Stars value={avg} /><span className="rating-val">{avg}</span>
          <span className="rating-cnt">({count})</span>
        </div>
      </div>

      <div className="ac-stats">
        <div className="ac-stat"><div className="ac-stat-val">{expert.ipo_experience ? `${expert.years_of_experience}+ yrs` : "New"}</div><div className="ac-stat-lbl">IPO experience</div></div>
        <div className="ac-stat"><div className="ac-stat-val">{dealsFor(expert.expert_id)}</div><div className="ac-stat-lbl">SME IPOs done</div></div>
        <div className="ac-stat"><div className="ac-stat-val" style={{ color: "#1D9E75" }}>{verifiedAreas.length}/{areas.length || 1}</div><div className="ac-stat-lbl">Areas verified</div></div>
        <div className="ac-stat"><div className="ac-stat-val">{expert.city}</div><div className="ac-stat-lbl">Based in</div></div>
      </div>

      {areas.length > 1 && (
        <div className="ac-tags">{expert.expertise_labels?.map((l, i) => <span className="ac-tag" key={i}>{l}</span>)}</div>
      )}

      <div className="ac-quote">"{quoteFor(expert.expert_id)}" — recent issuer</div>

      <div className="ac-bottom">
        <div className="ac-actions">
          <button className="btn-primary btn-msg" onClick={() => { onToggle(expert.expert_id, "contact"); }} data-testid={`adv-contact-${expert.expert_id}`}><i className="ti ti-message-2" style={{ fontSize: 12 }} /> Message</button>
          <Link className="btn-outline" to={`/advisor/${expert.expert_id}`} data-testid={`adv-view-${expert.expert_id}`}>
            View profile
          </Link>
        </div>
        <span className="ac-resp"><i className="ti ti-clock-hour-4" /> {respFor(expert.expert_id)}</span>
      </div>

      {expanded && (
        <div className="detail-overlay" data-testid={`adv-detail-${expert.expert_id}`} style={{ marginTop: 14 }}>
          <div className="do-tabs">
            <div className={`do-tab ${tab === "verify" ? "on" : ""}`} onClick={() => setTab("verify")} data-testid={`adv-tab-verify-${expert.expert_id}`}>Verification &amp; identifiers</div>
            <div className={`do-tab ${tab === "expertise" ? "on" : ""}`} onClick={() => setTab("expertise")}>Expertise</div>
            <div className={`do-tab ${tab === "contact" ? "on" : ""}`} onClick={() => setTab("contact")} data-testid={`adv-tab-contact-${expert.expert_id}`}>Contact</div>
          </div>

          {tab === "verify" && (
            <div data-testid={`adv-verif-${expert.expert_id}`}>
              {areas.length === 0 && <div className="ac-desc">No expertise areas on file.</div>}
              {areas.map((aid) => {
                const spec = verifCache[aid];
                const isVerified = verifiedAreas.includes(aid);
                const portal = VERIFY_PORTAL[aid];
                return (
                  <div className="vf-area" key={aid}>
                    <div className="vf-area-h">
                      <span>{expert.expertise_labels?.[areas.indexOf(aid)] || aid}</span>
                      <span className={`vf-status ${isVerified ? "ok" : "pend"}`}>{isVerified ? "✓ Verified" : "⏳ Pending"}</span>
                    </div>
                    {spec ? (
                      <>
                        <div className="vf-reg"><i className="ti ti-gavel" /> {spec.regulator}</div>
                        <div className="vf-grp-t">Primary identifiers</div>
                        <div className="vf-ids">
                          {spec.primary.map((f) => (
                            <div className="vf-id" key={f.key}><span className="vf-id-label">{f.label}</span><span className="vf-id-hint">{f.hint}</span></div>
                          ))}
                        </div>
                        <div className="vf-grp-t">Secondary / firm identifiers</div>
                        <div className="vf-ids">
                          {spec.secondary.map((f) => (
                            <div className="vf-id" key={f.key}><span className="vf-id-label">{f.label}</span><span className="vf-id-hint">{f.hint}</span></div>
                          ))}
                        </div>
                        {portal && <a className="vf-link" href={portal.url} target="_blank" rel="noreferrer"><i className="ti ti-external-link" /> Verify at {portal.name}</a>}
                      </>
                    ) : <div className="vf-reg">Loading identifiers…</div>}
                  </div>
                );
              })}
            </div>
          )}

          {tab === "expertise" && (
            <div data-testid={`adv-expertise-${expert.expert_id}`}>
              <div className="ac-tags" style={{ marginBottom: 12 }}>
                {(expert.expertise_labels || []).map((l, i) => <span className="ac-tag" key={i}>{l}</span>)}
              </div>
              <div className="fee-row"><div className="fee-lbl">Mobile</div><div className="fee-val">{expert.mobile || "—"}</div></div>
              {expert.email && <div className="fee-row"><div className="fee-lbl">Email</div><div className="fee-val">{expert.email}</div></div>}
              <div className="fee-row"><div className="fee-lbl">Location</div><div className="fee-val">{expert.city}{expert.state ? `, ${expert.state}` : ""}</div></div>
              <div className="fee-row"><div className="fee-lbl">Works</div><div className="fee-val">{expert.work_location === "city_only" ? "City only (in-person)" : "Pan-India (online)"}</div></div>
            </div>
          )}

          {tab === "contact" && (
            <div data-testid={`adv-contactform-${expert.expert_id}`}>
              <div className="rv-form-title">Send {expert.full_name} a mandate enquiry</div>
              <input className="rv-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid={`adv-contact-subject-${expert.expert_id}`} />
              <textarea className="rv-textarea" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Tell them about your IPO and what you need…" data-testid={`adv-contact-body-${expert.expert_id}`} />
              <button className="btn-primary" onClick={sendContact} data-testid={`adv-contact-send-${expert.expert_id}`}>Send message</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Public "Message" enquiry modal — collects the enquirer's contact + message and
 * posts to the advisor enquiry endpoint (→ Admin Center lead + email to advisor). */
const AmxEnquiryModal = ({ expert, onClose }) => {
  const [f, setF] = useState({ name: "", email: "", mobile: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { if (expert) setF({ name: "", email: "", mobile: "", message: "" }); }, [expert]);
  if (!expert) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const send = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) { toast.error("Please add your name, email and a short message."); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/matchmaker/experts/${expert.expert_id}/enquiry`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: f.name, email: f.email, mobile: f.mobile, message: f.message, subject: `Enquiry for ${expert.full_name}` }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Could not send your enquiry. Please try again."); setSending(false); return; }
      toast.success(d.message || `Enquiry sent to ${expert.full_name}.`);
      onClose();
    } catch { toast.error("Connection error. Please try again."); setSending(false); }
  };
  return (
    <div className="amx-enq-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="amx-enq" data-testid="amx-enquiry-modal">
        <div className="amx-enq-head">
          <div>
            <div className="amx-enq-t">Message {expert.full_name}</div>
            <div className="amx-enq-s">{expert.expertise_labels?.[0] || "IPO Advisor"} · {expert.city || "India"}</div>
          </div>
          <button className="amx-enq-x" onClick={onClose} data-testid="amx-enquiry-close" aria-label="Close"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
        <div className="amx-enq-body">
          <div className="amx-enq-grid">
            <div><label>Your name</label><input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" data-testid="amx-enq-name" /></div>
            <div><label>Email</label><input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" data-testid="amx-enq-email" /></div>
          </div>
          <div><label>Mobile (optional)</label><input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" data-testid="amx-enq-mobile" /></div>
          <div><label>Message</label><textarea rows={4} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Describe what you need, timeline and any specifics…" data-testid="amx-enq-message" /></div>
          <p className="amx-enq-note"><i className="ti ti-info-circle" aria-hidden="true" /> Your enquiry reaches our team and is emailed to the advisor.</p>
        </div>
        <div className="amx-enq-foot">
          <button className="amx-enq-cancel" onClick={onClose}>Cancel</button>
          <button className="amx-enq-send" disabled={sending} onClick={send} data-testid="amx-enq-send">{sending ? "Sending…" : "Send enquiry"}</button>
        </div>
      </div>
    </div>
  );
};

export const AdvisorMarketplacePanel = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [allExperts, setAllExperts] = useState([]);
  const [experts, setExperts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState(0);                 // 0..2 directory, 3 board
  const [search, setSearch] = useState("");
  const [expertise, setExpertise] = useState("");
  const [city, setCity] = useState("");
  const [workLoc, setWorkLoc] = useState("");
  const [premiumOnly, setPremiumOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [verifCache, setVerifCache] = useState({});
  const [contactId, setContactId] = useState(null);

  const [boardItems, setBoardItems] = useState({});  // role_id -> {status, note}
  const [areaCounts, setAreaCounts] = useState({});

  const areaLabel = (id) => areas.find((a) => a.id === id)?.label || id;

  useEffect(() => {
    fetch(`${API_URL}/api/matchmaker/expert/expertise-areas`).then((r) => r.json()).then((d) => setAreas(d.areas || [])).catch(() => {});
    fetch(`${API_URL}/api/matchmaker/expert/major-cities`).then((r) => r.json()).then((d) => setCities(d.cities || [])).catch(() => {});
    fetch(`${API_URL}/api/matchmaker/experts/browse`, { credentials: "include" }).then((r) => r.json()).then((d) => setAllExperts(d.experts || [])).catch(() => {});
    fetch(`${API_URL}/api/matchmaker/team-board`, { credentials: "include" }).then((r) => r.json()).then((d) => { setBoardItems(d.items || {}); setAreaCounts(d.area_counts || {}); }).catch(() => {});
    fetch(`${API_URL}/api/project-board/carousel`, { credentials: "include" }).then((r) => r.json()).then((d) => setProjItems(d.items || [])).catch(() => {});
  }, []);

  const fetchExperts = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.append("search", search);
      if (expertise) p.append("expertise", expertise);
      if (city) p.append("city", city);
      if (workLoc) p.append("work_location", workLoc);
      if (premiumOnly) p.append("premium_only", "true");
      if (verifiedOnly) p.append("verified_only", "true");
      const res = await fetch(`${API_URL}/api/matchmaker/experts/browse?${p.toString()}`, { credentials: "include" });
      const d = await res.json();
      setExperts(d.experts || []);
    } catch { setExperts([]); }
    setLoading(false);
  }, [search, expertise, city, workLoc, premiumOnly, verifiedOnly]);

  useEffect(() => { const t = setTimeout(fetchExperts, 250); return () => clearTimeout(t); }, [fetchExperts]);

  // Sort per category tab.
  const sorted = [...experts].sort((a, b) => {
    if (tab === 0) { // Recommended: premium+verified first
      const sa = (a.is_premium ? 2 : 0) + (a.is_verified ? 1 : 0);
      const sb = (b.is_premium ? 2 : 0) + (b.is_verified ? 1 : 0);
      return sb - sa;
    }
    if (tab === 1) return Number(ratingFor(b.expert_id).avg) - Number(ratingFor(a.expert_id).avg); // Top rated
    if (tab === 2) return (b.created_at || "").localeCompare(a.created_at || ""); // Recently active
    return 0;
  });

  const toggleExpand = async (id, forceTab) => {
    const willOpen = expandedId !== id || forceTab;
    setContactId(forceTab === "contact" ? id : null);
    setExpandedId((cur) => (cur === id && !forceTab ? null : id));
    if (willOpen) {
      const e = experts.find((x) => x.expert_id === id);
      const missing = (e?.expertise_areas || []).filter((a) => !verifCache[a]);
      missing.forEach(async (aid) => {
        try {
          const r = await fetch(`${API_URL}/api/matchmaker/expert/verification-fields/${aid}`);
          if (r.ok) { const spec = await r.json(); setVerifCache((c) => ({ ...c, [aid]: spec })); }
        } catch {}
      });
    }
  };

  // ── Team board ──
  const saveBoard = async (items) => {
    try {
      await fetch(`${API_URL}/api/matchmaker/team-board`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
    } catch {}
  };
  const setRoleStatus = (roleId, status) => {
    const next = { ...boardItems, [roleId]: { ...(boardItems[roleId] || {}), status } };
    setBoardItems(next);
    saveBoard(next);
  };
  const findExperts = (role) => {
    if (!role.area) { toast.info(`${role.label} is an internal board / KMP appointment — track its status here.`); return; }
    setExpertise(role.area); setTab(0);
    toast.success(`Showing ${areaLabel(role.area)} advisors`);
  };

  const mandatoryRoles = ROLE_CATALOG.filter((r) => r.mandatory);
  const isCovered = (roleId) => ["engaged", "filled"].includes(boardItems[roleId]?.status);
  const filledCount = mandatoryRoles.filter((r) => isCovered(r.id)).length;
  const coverage = Math.round((filledCount / mandatoryRoles.length) * 100);

  const featured = [...allExperts].sort((a, b) => {
    const sa = (a.is_premium ? 2 : 0) + (a.is_verified ? 1 : 0);
    const sb = (b.is_premium ? 2 : 0) + (b.is_verified ? 1 : 0);
    return sb - sa || Number(ratingFor(b.expert_id).avg) - Number(ratingFor(a.expert_id).avg);
  }).slice(0, 3);
  const openExpert = (id, mode) => { clearAll(); setTab(0); setTimeout(() => toggleExpand(id, mode), 320); };
  const goPremium = () => navigate(PREMIUM_UPGRADE_ROUTE);
  const hasFilters = search || expertise || city || workLoc || premiumOnly || verifiedOnly;
  const clearAll = () => { setSearch(""); setExpertise(""); setCity(""); setWorkLoc(""); setPremiumOnly(false); setVerifiedOnly(false); };
  const CAT_TABS = ["Recommended for you", "Top rated", "Recently active", `My DRHP team (${filledCount}/${mandatoryRoles.length})`];

  // Standalone-page helpers (redesigned public marketplace — Image 5 + 6).
  const resultsRef = useRef(null);
  const [sortMode, setSortMode] = useState("latest");
  const [projItems, setProjItems] = useState([]);
  const [enquiryFor, setEnquiryFor] = useState(null);
  const scrollToResults = () => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pickArea = (area) => { setExpertise((v) => (v === area ? "" : area)); setSearch(""); setTab(0); setTimeout(scrollToResults, 140); };
  const amxSorted = [...experts].sort((a, b) => {
    if (sortMode === "latest") return (b.created_at || "").localeCompare(a.created_at || "");
    if (sortMode === "rating") return Number(ratingFor(b.expert_id).avg) - Number(ratingFor(a.expert_id).avg);
    if (sortMode === "deals") return dealsFor(b.expert_id) - dealsFor(a.expert_id);
    if (sortMode === "city") return (a.city || "").localeCompare(b.city || "");
    const sa = (a.is_premium ? 2 : 0) + (a.is_verified ? 1 : 0);
    const sb = (b.is_premium ? 2 : 0) + (b.is_verified ? 1 : 0);
    return sb - sa || Number(ratingFor(b.expert_id).avg) - Number(ratingFor(a.expert_id).avg);
  });

  if (!embedded) {
    const activeAreaLabel = expertise ? areaLabel(expertise) : "";
    return (
      <div className="mp amx">
        <h2 className="sr-only">Advisor Marketplace — find verified IPO, compliance and finance experts by city, name or expertise.</h2>

        {/* ── Hero + functional search (Image 5) ── */}
        <section className="amx-hero">
          <span className="amx-eyebrow"><i className="ti ti-users-group" aria-hidden="true" /> OPEN TO EVERYONE · NO IPO NAVIGATOR ACCOUNT NEEDED</span>
          <h1 className="amx-h1">Find the right expert for your next project</h1>
          <p className="amx-lead">A marketplace of verified IPO, compliance and finance professionals — for founders, CA firms and businesses of any kind. Browse, compare and connect directly.</p>

          <div className="amx-search">
            <i className="ti ti-search" aria-hidden="true" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setExpertise(""); if (tab === 3) setTab(0); }}
              onKeyDown={(e) => { if (e.key === "Enter") scrollToResults(); }}
              placeholder="Try 'SEBI compliance', 'CA', 'Merchant Banker', 'Mumbai'…"
              data-testid="amx-search-input"
            />
            <button className="amx-search-btn" onClick={scrollToResults} data-testid="amx-search-btn">Search</button>
          </div>

          <div className="amx-popular">
            <span>Popular:</span>
            {POPULAR.map((p) => (
              <button key={p.label} className={`amx-tag ${expertise === p.area ? "on" : ""}`} onClick={() => pickArea(p.area)} data-testid={`amx-popular-${p.area}`}>{p.label}</button>
            ))}
          </div>

          <div className="amx-cat-head">
            <h3>Browse by category</h3>
            <p>Every kind of expertise a growing company needs</p>
          </div>
          <div className="amx-cat-grid">
            {CATEGORY_CARDS.map((c) => {
              const cnt = areaCounts[c.area] || 0;
              return (
                <button key={c.label} className={`amx-cat ${expertise === c.area ? "on" : ""}`} onClick={() => pickArea(c.area)} data-testid={`amx-cat-${c.area}`}>
                  <span className="amx-cat-ic"><i className={`ti ${c.icon}`} aria-hidden="true" /></span>
                  <span className="amx-cat-nm">{c.label}</span>
                  <span className="amx-cat-ct">{cnt > 0 ? `${cnt} expert${cnt !== 1 ? "s" : ""}` : "Browse experts"}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Live projects carousel (below Browse by category) ── */}
        {projItems.length > 0 && (
          <section
            className="amx-proj-carousel"
            data-testid="amx-proj-carousel"
            role="button"
            tabIndex={0}
            onClick={() => navigate("/published-projects")}
            onKeyDown={(e) => { if (e.key === "Enter") navigate("/published-projects"); }}
          >
            <div className="amx-pc-head">
              <span className="amx-pc-title"><i className="ti ti-briefcase" aria-hidden="true" /> Live projects looking for advisors</span>
              <button className="amx-pc-viewall" onClick={(e) => { e.stopPropagation(); navigate("/published-projects"); }} data-testid="amx-carousel-viewall">
                View all projects <i className="ti ti-arrow-right" aria-hidden="true" />
              </button>
            </div>
            <div className="amx-pc-track-wrap">
              <div className="amx-pc-track">
                {[...projItems, ...projItems].map((it, i) => (
                  <span className="amx-pc-item" key={i}>
                    <i className="ti ti-point-filled" aria-hidden="true" />
                    <b>{it.title}</b><span className="amx-pc-req">— {it.short_summary}</span>
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Premium rider (on both marketplace & projects pages) ── */}
        <section className="amx-prem-rider" data-testid="amx-premium-rider">
          <div className="amx-pr-l">
            <span className="amx-pr-ic"><i className="ti ti-crown" aria-hidden="true" /></span>
            <div>
              <div className="amx-pr-t">Become a Premium User to view the entire project details — including contact details</div>
              <div className="amx-pr-d">Non-premium members see only the poster's name and project scope. Upgrade to unmask budgets, timelines, full briefs and direct contact.</div>
            </div>
          </div>
          <button className="amx-pr-btn" onClick={() => navigate("/published-projects?premium=1")} data-testid="amx-rider-upgrade">
            <i className="ti ti-brand-cashapp" aria-hidden="true" /> Become Premium
          </button>
        </section>

        {/* ── Results — 2 square cards per row, ~6 rows then scroll, no horizontal scroll ── */}
        <section className="amx-results" ref={resultsRef}>
          <div className="amx-results-head">
            <div className="amx-results-count" data-testid="amx-results-count">
              {loading ? "Loading experts…" : `${amxSorted.length} expert${amxSorted.length !== 1 ? "s" : ""} found`}
              {activeAreaLabel && <span className="amx-active-filter">{activeAreaLabel} <button onClick={() => setExpertise("")} data-testid="amx-clear-filter" aria-label="Clear filter">×</button></span>}
              {search && <span className="amx-active-filter">“{search}” <button onClick={() => setSearch("")} aria-label="Clear search">×</button></span>}
            </div>
            <div className="amx-head-right">
              <div className="amx-sort" data-testid="amx-sort">
                <span className="amx-sort-lbl">Sort:</span>
                {[["latest", "Latest Registered"], ["premium", "Premium"], ["rating", "Top rated"], ["deals", "Most SME IPOs"], ["city", "Nearest city"]].map(([v, l]) => (
                  <button key={v} className={`amx-sort-btn ${sortMode === v ? "on" : ""}`} onClick={() => setSortMode(v)} data-testid={`amx-sort-${v}`}>{l}</button>
                ))}
              </div>
              {hasFilters && <button className="amx-clear-all" onClick={clearAll} data-testid="amx-clear-all">Clear all</button>}
            </div>
          </div>

          {!loading && amxSorted.length === 0 ? (
            <div className="amx-empty" data-testid="amx-empty">
              No experts match yet. <span onClick={() => navigate("/advisor-onboarding")}>Invite an advisor to register →</span>
            </div>
          ) : (
            <div className="amx-grid" data-testid="amx-grid">
              {amxSorted.map((e, i) => {
                const col = AV_COLORS[i % AV_COLORS.length];
                const r = ratingFor(e.expert_id);
                return (
                  <Link className="amx-card" key={e.expert_id} to={`/advisor/${e.expert_id}`} data-testid={`amx-card-${e.expert_id}`}>
                    {e.is_premium && <span className="amx-card-crown"><i className="ti ti-crown" aria-hidden="true" /> Premium</span>}
                    <div className="amx-card-top">
                      <div className="amx-av" style={{ background: col.bg, color: col.c }}>{initials(e.full_name)}</div>
                      <div className="amx-idnt">
                        <div className="amx-name">{e.full_name}</div>
                        <div className="amx-role">{e.expertise_labels?.[0] || "IPO Advisor"}</div>
                        <div className="amx-meta"><i className="ti ti-map-pin" aria-hidden="true" /> {e.city || "India"} · ★ {r.avg} ({r.count})</div>
                      </div>
                    </div>
                    <div className="amx-badges">
                      <span className="amx-mini sebi">SEBI aligned</span>
                      {e.is_verified && <span className="amx-mini ver"><i className="ti ti-rosette-discount-check" aria-hidden="true" /> Verified</span>}
                      <span className="amx-mini pan">{e.work_location === "city_only" ? "City only" : "Pan-India"}</span>
                    </div>
                    <div className="amx-proof">{e.ipo_experience ? `${e.years_of_experience}+ yrs experience` : "New advisor"} · {dealsFor(e.expert_id)} SME IPOs</div>
                    <div className="amx-btns">
                      <button className="amx-b amx-b-msg" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setEnquiryFor(e); }} data-testid={`amx-msg-${e.expert_id}`}><i className="ti ti-message-2" aria-hidden="true" /> Message</button>
                      <button className="amx-b amx-b-prof" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); navigate(`/advisor/${e.expert_id}`); }} data-testid={`amx-view-${e.expert_id}`}>View profile</button>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Section 6 — how it works + become an advisor ── */}
        <section className="amx-how">
          <h3 className="amx-how-h">How it works</h3>
          <p className="amx-how-sub">From search to signed engagement, in three steps</p>
          <div className="amx-steps">
            <div className="amx-step"><span className="amx-step-n">01</span><div className="amx-step-t">Post your requirement</div><div className="amx-step-d">Tell us what you need — DRHP drafting, SEBI compliance, valuation or a full IPO team.</div></div>
            <div className="amx-step"><span className="amx-step-n">02</span><div className="amx-step-t">Browse &amp; compare experts</div><div className="amx-step-d">Review verified profiles, ratings, experience and location — all in one place.</div></div>
            <div className="amx-step"><span className="amx-step-n">03</span><div className="amx-step-t">Connect &amp; discuss</div><div className="amx-step-d">Message shortlisted experts directly and take your project forward.</div></div>
          </div>
        </section>

        <section className="amx-cta" data-testid="amx-become-advisor-band">
          <div>
            <h3>Are you an advisor, CA, or merchant banker?</h3>
            <p>List your services on the Advisor Marketplace and get discovered by founders and firms across India — whether or not they use IPO Navigator.</p>
          </div>
          <button className="amx-cta-btn" onClick={() => navigate("/advisor-onboarding")} data-testid="amx-become-advisor-btn"><i className="ti ti-arrow-right" aria-hidden="true" /> Become an Advisor</button>
        </section>

        <MarketplaceFooterStrip />

        <AmxEnquiryModal expert={enquiryFor} onClose={() => setEnquiryFor(null)} />
      </div>
    );
  }

  return (
      <div className="mp">
        <h2 className="sr-only">Advisor Marketplace — discover, verify and engage every SEBI-registered IPO advisor, director and KMP needed to build a DRHP.</h2>

        <div className="mp-header">
          {!embedded && <div className="mp-title">Advisor Marketplace</div>}
          <div className="mp-sub">
            Discover, verify and engage every specialist your DRHP needs — BRLMs, auditors, legal counsel,
            registrars, valuers, directors and KMP. Track your whole IPO team in one place.
            <span style={{ marginLeft: 8 }} />
            <button className="filter-chip" onClick={() => navigate("/advisor-onboarding")} data-testid="adv-join-as-advisor" style={{ borderColor: "#0F6E56", color: "#0F6E56" }}>+ Join as an advisor</button>
            <button className="filter-chip" onClick={goPremium} data-testid="adv-upgrade-premium-header" style={{ borderColor: "#854F0B", color: "#854F0B", fontWeight: 600 }}>👑 Upgrade to Premium Advisor</button>
          </div>

          <div className="filters">
            <div className="search-box"><i className="ti ti-search" aria-hidden="true" /><input type="text" placeholder="Search by name or city..." value={search} onChange={(e) => { setSearch(e.target.value); if (tab === 3) setTab(0); }} data-testid="adv-mp-search" /></div>
            {CHIPS.map((c) => (
              <button key={c.id} className={`filter-chip ${(expertise || "") === c.area ? "on" : ""}`} onClick={() => { setExpertise(c.area); if (tab === 3) setTab(0); }} data-testid={`filter-chip-${c.id}`}>{c.label}</button>
            ))}
          </div>

          <div className="category-tabs">
            {CAT_TABS.map((t, i) => (
              <div key={i} className={`cat-tab ${tab === i ? "on" : ""}`} onClick={() => setTab(i)} data-testid={`cat-tab-${i}`}>{t}</div>
            ))}
          </div>
        </div>

        <div className="mp-body">
          {/* Left filters */}
          <div className="left-filters">
            <div style={{ paddingTop: 14 }} />
            <div className="lf-section">
              <div className="lf-title">Advisor role</div>
              {ROLE_CATALOG.filter((r) => r.area).map((r) => (
                <div key={r.area} className="lf-item" onClick={() => { setExpertise((v) => (v === r.area ? "" : r.area)); if (tab === 3) setTab(0); }} data-testid={`lf-role-${r.area}`}>
                  <div className={`lf-check ${expertise === r.area ? "on" : ""}`}>{expertise === r.area && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                  <div className="lf-label">{r.label}</div>
                  <div className="lf-count">{areaCounts[r.area] || 0}</div>
                </div>
              ))}
            </div>

            <div className="lf-section">
              <div className="lf-title">Work location</div>
              <div className="lf-item" onClick={() => setWorkLoc((v) => (v === "city_only" ? "" : "city_only"))} data-testid="lf-work-city">
                <div className={`lf-check ${workLoc === "city_only" ? "on" : ""}`}>{workLoc === "city_only" && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                <div className="lf-label">My city only</div>
              </div>
              <div className="lf-item" onClick={() => setWorkLoc((v) => (v === "pan_india" ? "" : "pan_india"))} data-testid="lf-work-pan">
                <div className={`lf-check ${workLoc === "pan_india" ? "on" : ""}`}>{workLoc === "pan_india" && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                <div className="lf-label">Pan-India · online</div>
              </div>
            </div>

            <div className="lf-section">
              <div className="lf-title">Listing &amp; verification</div>
              <div className="lf-item" onClick={() => setVerifiedOnly((v) => !v)} data-testid="lf-verified">
                <div className={`lf-check ${verifiedOnly ? "on" : ""}`}>{verifiedOnly && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                <div className="lf-label">SEBI verified only</div>
              </div>
              <div className="lf-item" onClick={() => setPremiumOnly((v) => !v)} data-testid="lf-premium">
                <div className={`lf-check ${premiumOnly ? "on" : ""}`}>{premiumOnly && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                <div className="lf-label">Premium only</div>
              </div>
            </div>

            <div className="lf-section">
              <div className="lf-title">City</div>
              {cities.slice(0, 12).map((c) => (
                <div key={c} className="lf-item" onClick={() => setCity((v) => (v === c ? "" : c))} data-testid={`lf-city-${c}`}>
                  <div className={`lf-check ${city === c ? "on" : ""}`}>{city === c && <i className="ti ti-check" style={{ fontSize: 9, color: "#E6F1FB" }} />}</div>
                  <div className="lf-label">{c}</div>
                </div>
              ))}
            </div>

            {hasFilters && <button className="clear-btn" onClick={clearAll} data-testid="adv-clear-filters">Clear all filters ×</button>}
          </div>

          {/* Cards / Board */}
          <div className="cards-area">
            {tab !== 3 ? (
              <>
                {featured.length > 0 && (
                  <div className="feat-band" data-testid="adv-featured-band">
                    <div className="feat-band-lbl"><i className="ti ti-crown" aria-hidden="true" /> Featured advisors · premium &amp; verified</div>
                    <div className="feat-grid">
                      {featured.map((e, i) => {
                        const col = AV_COLORS[i % AV_COLORS.length];
                        const r = ratingFor(e.expert_id);
                        return (
                          <Link className="fcard" key={e.expert_id} to={`/advisor/${e.expert_id}`} data-testid={`adv-featured-${e.expert_id}`}>
                            <div className="fc-crown"><i className="ti ti-crown" style={{ fontSize: 9 }} aria-hidden="true" /> {e.is_premium ? "Featured" : "Verified"}</div>
                            <div className="fc-top"><div className="fc-av" style={{ background: col.bg, color: col.c }}>{initials(e.full_name)}</div><div><div className="fc-n">{e.full_name}</div><div className="fc-r">{e.expertise_labels?.[0] || "IPO Advisor"} · {e.city}</div><div className="fc-rate">★ {r.avg} ({r.count})</div></div></div>
                            <div className="fc-badges"><span className="mini m-sebi">SEBI aligned</span>{e.is_verified && <span className="mini m-ver">✓ Verified</span>}{e.work_location === "pan_india" && <span className="mini m-pan">Pan-India</span>}</div>
                            <div className="fc-proof">{e.ipo_experience ? <><b>{e.years_of_experience}+ yrs</b> · </> : null}<b>{dealsFor(e.expert_id)} SME IPOs</b> · {(e.expertise_labels?.[0] || "IPO advisor").toLowerCase()}</div>
                            <div className="fc-btns"><button className="fb fb-y" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); openExpert(e.expert_id, "contact"); }} data-testid={`adv-featured-msg-${e.expert_id}`}>Message</button><button className="fb fb-n" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); navigate(`/advisor/${e.expert_id}`); }} data-testid={`adv-featured-profile-${e.expert_id}`}>Profile</button></div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "12px 0 8px" }} data-testid="adv-results-count">
                  {loading ? "Loading advisors…" : `${sorted.length} advisor${sorted.length !== 1 ? "s" : ""} found`}
                </div>
                {!loading && sorted.length === 0 ? (
                  <div className="empty-state" data-testid="adv-empty">
                    No advisors match your filters yet.{" "}
                    <span style={{ color: "#0F6E56", cursor: "pointer" }} onClick={() => navigate("/advisor-onboarding")}>Invite an advisor to register →</span>
                  </div>
                ) : (
                  sorted.map((e, i) => (
                    <ExpertCard key={e.expert_id} expert={e} idx={i} featured={tab === 0 && i === 0 && e.is_premium}
                      expanded={expandedId === e.expert_id} onToggle={toggleExpand} onProfile={(id) => navigate(`/advisor/${id}`)} verifCache={verifCache}
                      role={e.expertise_labels?.[0]} startTab={contactId === e.expert_id ? "contact" : "verify"} />
                  ))
                )}
              </>
            ) : (
              <div className="tb-wrap" data-testid="team-board">
                <div className="tb-head">
                  <div className="tb-ring" style={{ background: `conic-gradient(#185FA5 ${coverage * 3.6}deg, #E5E8EB 0deg)` }}>
                    <div className="tb-ring-in"><span data-testid="tb-coverage-pct">{coverage}%</span></div>
                  </div>
                  <div>
                    <div className="tb-head-t">Your DRHP advisory team</div>
                    <div className="tb-head-d">{filledCount} of {mandatoryRoles.length} mandatory roles engaged. Track every SME, director &amp; KMP needed to file your DRHP — set each role's status and find verified experts.</div>
                  </div>
                </div>

                {CATEGORIES.map((cat) => (
                  <div className="tb-cat" key={cat}>
                    <div className="tb-cat-t">{cat}</div>
                    {ROLE_CATALOG.filter((r) => r.category === cat).map((r) => {
                      const status = boardItems[r.id]?.status || "open";
                      const cnt = r.area ? (areaCounts[r.area] || 0) : null;
                      return (
                        <div className="tb-role" key={r.id} data-testid={`tb-role-${r.id}`}>
                          <div className="tb-role-main">
                            <div className="tb-role-name">
                              {r.label}
                              {r.mandatory ? <span className="tb-req">Mandatory</span> : <span className="tb-opt">Optional</span>}
                            </div>
                            <div className="tb-role-meta">
                              {r.area ? <><i className="ti ti-users" /> {cnt} verified expert{cnt !== 1 ? "s" : ""} available</> : <><i className="ti ti-briefcase" /> Internal board / KMP appointment</>}
                            </div>
                          </div>
                          <span className={`st-pill st-${status}`}>{STATUS_OPTS.find((s) => s.v === status)?.label}</span>
                          <select className="tb-status" value={status} onChange={(e) => setRoleStatus(r.id, e.target.value)} data-testid={`tb-status-${r.id}`}>
                            {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                          </select>
                          {r.area && <button className="tb-find" onClick={() => findExperts(r)} data-testid={`tb-find-${r.id}`}>Find experts →</button>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mp-upsell" data-testid="adv-upsell-banner">
          <div className="up-l">
            <div className="up-h"><i className="ti ti-crown" aria-hidden="true" /> Are you an advisor? Get seen first.</div>
            <div className="up-d">Premium listings appear in the Featured band and rank above standard results — where founders look first.</div>
          </div>
          <div className="up-stats">
            <div><div className="up-v">5×</div><div className="up-sl">more profile views</div></div>
            <div><div className="up-v">3×</div><div className="up-sl">more mandates</div></div>
          </div>
          <button className="up-btn" onClick={goPremium} data-testid="adv-upsell-upgrade">Upgrade to Premium →</button>
        </div>
      </div>
  );
};

const AdvisorMarketplace = () => {
  const navigate = useNavigate();
  useEffect(() => { logJourney("Advisor Marketplace", "Viewed", "Opened Advisor Marketplace"); }, []);
  return (
    <div className="adv-mp-root" data-testid="advisor-marketplace-page">
      <div className="mp-topbar">
        <div className="mp-left">
          <div className="mp-brand" onClick={() => navigate("/ipo-navigator")} data-testid="adv-mp-brand">IPO<span>Navigator</span></div>
          <button className="mp-navbtn" onClick={() => navigate("/home")} data-testid="mp-home-btn" title="Home Page"><i className="ti ti-home" aria-hidden="true" /><span className="mp-navbtn-t">Home Page</span></button>
          <button className="mp-navbtn oracle" onClick={() => navigate("/oracle")} data-testid="mp-ask-oracle" title="Ask Oracle {IPO.GPT}"><i className="ti ti-robot" aria-hidden="true" /><span className="mp-navbtn-t">Ask Oracle {"{IPO.GPT}"}</span></button>
        </div>
        <div className="mp-center">
          <button className="amx-post-project" onClick={() => navigate("/published-projects")} data-testid="amx-post-project-btn">
            <i className="ti ti-plus" aria-hidden="true" /> Post a Project
          </button>
        </div>
        <div className="mp-right">
          <button className="mp-cta mp-cta-join" onClick={() => navigate("/advisor-onboarding")} data-testid="adv-join-as-advisor-top"><i className="ti ti-user-plus" aria-hidden="true" /> Join as an advisor</button>
          <button className="mp-cta mp-cta-premium" onClick={() => navigate(PREMIUM_UPGRADE_ROUTE)} data-testid="adv-upgrade-premium-top"><i className="ti ti-crown" aria-hidden="true" /> Upgrade to Premium</button>
        </div>
      </div>
      <AdvisorMarketplacePanel />
    </div>
  );
};

export default AdvisorMarketplace;
